import { Row, Table as TanstackTable } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { useCatalogMultiSelectionContext } from 'src/contexts/CatalogMultiSelectionContext';
import {
  MultiSelectScope,
  NO_GROUP_ID,
  TableRow,
  useMultiSelectionContext,
} from 'src/contexts/MultiSelectionContext';
import { Checkbox } from 'src/core/interim/Checkbox';
import { PosHotkeys } from 'src/core/POS/PosHotkeys/PosHotkeys';
import { TableCellDiv } from 'src/core/ui/TableCellDiv';

interface TableSelectAllCheckboxProps<TData> {
  showSelectAllCheckbox?: boolean;
  data: TData[];
  getGroupId?: (data: TData) => string | null | undefined;
  getItemId?: (data: TData) => string;
  flattenedIdsOverride?: (number | string)[];
}

export function TableSelectAllCheckbox<TData>({
  showSelectAllCheckbox,
  data,
  getGroupId,
  getItemId,
  flattenedIdsOverride,
}: TableSelectAllCheckboxProps<TData>) {
  const { flattenedIds: flattenedIdsFromContext } =
    useCatalogMultiSelectionContext();

  const {
    toggleSelectAll,
    toggleGroup,
    totalItemsSelected,
    setGroupItems,
    selectionMode,
  } = useMultiSelectionContext();

  const { getItemToggleState } = useMultiSelectionContext();

  // Check selection state for items in THIS table only
  const selectedCount = data.filter((item, index) => {
    const itemId = getItemId ? getItemId(item) : String(index);
    const groupId = getGroupId
      ? (getGroupId(item) ?? NO_GROUP_ID)
      : NO_GROUP_ID;
    return getItemToggleState(itemId, groupId);
  }).length;

  const allSelected = selectedCount === data.length && data.length > 0;
  const someSelected = selectedCount > 0 && selectedCount < data.length;

  const toggleGroupWithOverride = useCallback(
    (groupId: string) => {
      if (flattenedIdsOverride) {
        if (totalItemsSelected >= flattenedIdsOverride.length) {
          setGroupItems(
            groupId,
            flattenedIdsOverride.reduce(
              (acc, id) => {
                acc[id] = false;
                return acc;
              },
              {} as Record<number | string, boolean>
            )
          );
        } else {
          setGroupItems(
            groupId,
            flattenedIdsOverride.reduce(
              (acc, id) => {
                acc[id] = true;
                return acc;
              },
              {} as Record<number | string, boolean>
            )
          );
        }
      }
    },
    [flattenedIdsOverride, setGroupItems, totalItemsSelected]
  );
  const onChange = useCallback(() => {
    if (
      selectionMode?.mode === MultiSelectScope.SingleGroup &&
      selectionMode?.groupId
    ) {
      if (flattenedIdsOverride != null) {
        toggleGroupWithOverride(selectionMode?.groupId);
      } else {
        toggleGroup(selectionMode?.groupId);
      }
    } else if (selectionMode?.mode === MultiSelectScope.AllGroups) {
      toggleSelectAll();
    }
  }, [
    flattenedIdsOverride,
    selectionMode,
    toggleGroup,
    toggleGroupWithOverride,
    toggleSelectAll,
  ]);
  return (
    <TableCellDiv align="left" showTooltip={false}>
      <PosHotkeys keyNames="ctrl+a, command+a" onHotkeyActivated={onChange}>
        <Checkbox
          labelPosition="right"
          indeterminate={someSelected}
          checked={allSelected || someSelected}
          onChange={onChange}
          checkboxSizeVariant="medium"
        />
      </PosHotkeys>
    </TableCellDiv>
  );
}

interface TableRowCheckboxProps<TData> {
  row: Row<TData>;
  table: TanstackTable<TData>;
  getGroupId?: (data: TData) => string | null | undefined;
  getItemId?: (data: TData) => string;
  isSelectionDisabled?: (data: TData) => string | undefined;
}

export function TableRowCheckbox<TData>({
  row,
  table,
  getGroupId,
  getItemId,
  isSelectionDisabled,
}: TableRowCheckboxProps<TData>) {
  const {
    setShiftKeyRowSelectionState,
    clearShiftKeySelection,
    getItemToggleState,
    setGroupItems,
    getGroupToggleState,
  } = useMultiSelectionContext();

  const data = row.original;
  const groupId = getGroupId ? (getGroupId(data) ?? NO_GROUP_ID) : NO_GROUP_ID;
  const itemId = getItemId ? getItemId(data) : row.id;
  const disabledReason = isSelectionDisabled
    ? isSelectionDisabled(data)
    : undefined;

  const isSelected = getItemToggleState(itemId, groupId);

  // Helper to recursively gather all item IDs from a row and its sub-rows
  const getAllItemIdsFromRow = (currentRow: Row<TData>): string[] => {
    const currentItemId = getItemId
      ? getItemId(currentRow.original)
      : currentRow.id;
    const ids = [currentItemId];

    // Recursively get IDs from sub-rows
    if (currentRow.subRows && currentRow.subRows.length > 0) {
      currentRow.subRows.forEach((subRow) => {
        ids.push(...getAllItemIdsFromRow(subRow));
      });
    }

    return ids;
  };

  const handleChange = () => {
    if (disabledReason) return;

    const newState = !isSelected;

    const groupState = getGroupToggleState(groupId);
    const isGroupSelected = groupState.isGroupSelected;
    const currentSelectedIds = groupState.items || [];

    const updatedItems: Record<string, boolean> = {};

    // If group is selected (select all mode), we need to get all visible items first
    if (isGroupSelected && !newState) {
      // Unchecking one item when "select all" is active
      // Get all visible rows and mark them as selected except the one being unchecked
      const allRows = table.getRowModel().rows;
      allRows.forEach((r) => {
        const rowItemId = getItemId ? getItemId(r.original) : r.id;
        if (rowItemId !== itemId) {
          updatedItems[rowItemId] = true;
        }
      });
    } else {
      // Normal selection mode - keep all currently selected items
      currentSelectedIds.forEach((id) => {
        updatedItems[id] = true;
      });

      // Get all item IDs including children when selecting/deselecting
      const allIds = getAllItemIdsFromRow(row);

      if (newState) {
        // Select this item and all its children
        allIds.forEach((id) => {
          updatedItems[id] = true;
        });
      } else {
        // Deselect this item and all its children
        allIds.forEach((id) => {
          delete updatedItems[id];
        });
      }
    }

    setGroupItems(groupId, updatedItems);

    if (newState) {
      const allRows = table.getRowModel().rows as unknown as TableRow[];
      setShiftKeyRowSelectionState(row.id, allRows, groupId);
    } else {
      clearShiftKeySelection();
    }
  };

  return (
    <TableCellDiv
      align="left"
      title={disabledReason}
      showTooltip={!!disabledReason}
      style={{ width: 'fit-content' }}
    >
      <Checkbox
        checked={isSelected}
        disabled={!!disabledReason}
        onChange={handleChange}
        checkboxSizeVariant="medium"
      />
    </TableCellDiv>
  );
}
